// @ts-strict-ignore
import * as React from 'react';
import { antGrey, colorWithAlpha, rwColors } from '../styles/globalStyles';
import { ThunderboltFilled } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { AppleAppStorePhasedReleaseState, AutomationAutoStagedRollout, PhasedRelease } from '../../types';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { PausedByBlackoutBadge } from '../monitoring/PhasedReleaseCard';
import { getRolloutDaysToPercentageFromAutomation } from '@/helpers';
import { selectAutomationById } from '@/selectors/selectors';
import { Nullable } from '@/typescriptHelpers';

interface PhasedReleaseProgressProps extends PhasedRelease {
    showPercent?: boolean;
    appId: string;
    progressStyle?: React.CSSProperties;
    phasedReleasePaused?: boolean;
    isAutoAccelerated?: boolean;
    isAutoHalted?: boolean;
    autoStagedRolloutAutomationId: Nullable<string>;
}

const BAR_HEIGHT = 6;
const BAR_MIN_FILL_PX = 6;

const UPCOMING_BAR_COLOR = antGrey.grey5;
const COMPLETED_DAY_TRACK_COLOR = colorWithAlpha(rwColors.primary, 0.2);

const iconForPhasedReleaseProgressAndDay = (phasedReleaseProgress: PhasedReleaseProgressProps, dayNumber: number) => {
    if (phasedReleaseProgress.dayNumber === dayNumber) {
        if (phasedReleaseProgress.isAutoAccelerated || phasedReleaseProgress.isAutoHalted) {
            return <ThunderboltFilled style={{ color: rwColors.primary }} />;
        } else if (phasedReleaseProgress.isOnBlackoutPeriod && !!phasedReleaseProgress.blackoutEndsAt) {
            return <PausedByBlackoutBadge phasedRelease={phasedReleaseProgress} isSizeSmall />;
        }
    }

    return <div style={{ minWidth: 14, height: 14 }} />;
};

const percentLabel = (percent: number, index: number) => {
    const truncated = Math.trunc(percent * 100) / 100;
    const ellipsis = truncated === percent ? '' : '...';
    return index === 0 ? `${truncated}${ellipsis}%` : `${truncated}${ellipsis}`;
};

const labelWidth = (chars: number) => `calc(${chars}ch + ${chars * 0.1}em)`;
const barFillWidth = (percent: number) => {
    const clamped = Math.min(Math.max(percent, 0), 100);
    if (clamped === 0) {
        return '0';
    }
    return `calc(${clamped}% + ${BAR_MIN_FILL_PX * (1 - clamped / 100)}px)`;
};

export const PhasedReleaseProgress: React.FC<PhasedReleaseProgressProps> = (props: PhasedReleaseProgressProps) => {
    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const daysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const customDays = daysToPercent
        ?.map((percent, index) => [index + 1, percent])
        .slice()
        .reverse() as [number, number][];

    const dayPairs: [number, number][] =
        customDays ?? [7, 6, 5, 4, 3, 2, 1].map((day) => [day, applePhasedReleasePercentByDay(day)]);

    const percentLabelChars = Math.max(
        '100%'.length,
        ...dayPairs.map(([, percent], index) => percentLabel(percent, index).length),
    );
    const dayLabelChars = Math.max(...dayPairs.map(([dayNumber]) => `Day ${dayNumber}`.length));

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: 330,
                position: 'relative',
            }}
        >
            {dayPairs.map(([dayNumber, phasedPercentByDay], index) => {
                let phasedPercent = phasedPercentByDay;
                const isCompletedDay = dayNumber <= props.dayNumber;
                const isCurrentDay = props.dayNumber === dayNumber;
                const isRolloutAccelerated =
                    props.isManuallyAcceleratedPhasedRollout || props.isRunwayAcceleratedRollout;

                if (isCurrentDay && isRolloutAccelerated && props.state === AppleAppStorePhasedReleaseState.Complete) {
                    // accelerated to 100% on this day
                    phasedPercent = 100;
                }

                let strokeColor = UPCOMING_BAR_COLOR;

                if (isCompletedDay) {
                    strokeColor = rwColors.primary;
                    if (isCurrentDay) {
                        if (props.phasedReleasePaused || props.isAutoHalted) {
                            strokeColor = rwColors.statusRed;
                        } else if (props.isAutoAccelerated) {
                            strokeColor = rwColors.statusGreen;
                        }
                    }
                }

                const isSkippedDay =
                    props.dayNumber < dayNumber &&
                    isRolloutAccelerated &&
                    props.state === AppleAppStorePhasedReleaseState.Complete;

                if (isSkippedDay) {
                    phasedPercent = 100;
                    strokeColor = UPCOMING_BAR_COLOR;
                }

                const trailColor = isCompletedDay ? COMPLETED_DAY_TRACK_COLOR : antGrey.grey4;
                const labelStyle: React.CSSProperties = {
                    color: isCurrentDay ? strokeColor : antGrey.grey7,
                    fontSize: 10,
                    lineHeight: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                };

                return (
                    <div
                        key={dayNumber}
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        {iconForPhasedReleaseProgressAndDay(props, dayNumber)}
                        <Tooltip placement="right" title={`${phasedPercent}%`}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    height: 16,
                                    flexGrow: 1,
                                }}
                            >
                                <Text style={{ ...labelStyle, marginLeft: 8, width: labelWidth(dayLabelChars) }}>
                                    Day {dayNumber}
                                </Text>
                                <div
                                    style={{
                                        ...props.progressStyle,
                                        flex: 1,
                                        minWidth: 0,
                                        height: BAR_HEIGHT,
                                        borderRadius: BAR_HEIGHT,
                                        backgroundColor: trailColor,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        style={{
                                            height: '100%',
                                            borderRadius: BAR_HEIGHT,
                                            backgroundColor: strokeColor,
                                            width: barFillWidth(phasedPercent),
                                        }}
                                    />
                                </div>
                                {props.showPercent !== false && (
                                    <Text
                                        style={{
                                            ...labelStyle,
                                            marginRight: 8,
                                            fontVariantNumeric: 'tabular-nums',
                                            width: labelWidth(percentLabelChars),
                                        }}
                                    >
                                        {percentLabel(phasedPercent, index)}
                                    </Text>
                                )}
                            </div>
                        </Tooltip>
                    </div>
                );
            })}
        </div>
    );
};

export const applePhasedReleasePercentByDay = (dayNumber: number) => {
    if (dayNumber === 0) {
        return 0;
    } else if (dayNumber === 1) {
        return 1;
    } else if (dayNumber === 2) {
        return 2;
    } else if (dayNumber === 3) {
        return 5;
    } else if (dayNumber === 4) {
        return 10;
    } else if (dayNumber === 5) {
        return 20;
    } else if (dayNumber === 6) {
        return 50;
    } else if (dayNumber === 7) {
        return 100;
    } else {
        return 100;
    }
};
